export interface IntercompanyQueryLoadFlags {
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    isPlaceholderData?: boolean;
}

export interface IntercompanyTransactionsPageLoadState {
    shouldShowError: boolean;
    shouldShowLoader: boolean;
    shouldShowReport: boolean;
    shouldShowStaleOverlay: boolean;
    shouldShowMissingGroup: boolean;
}

interface IntercompanyReportQueryGate {
    workspaceId?: string;
    consolidationGroupId?: string;
    startDate?: Date;
    endDate?: Date;
}

interface GetIntercompanyTransactionsPageLoadStateParams {
    hasRequiredReportParams: boolean;
    hasWorkspaceId: boolean;
    isResolvingGroup: boolean;
    hasConsolidationGroup: boolean;
    detail: IntercompanyQueryLoadFlags;
    exceptions: IntercompanyQueryLoadFlags;
    hydration: IntercompanyQueryLoadFlags;
}

export function getIntercompanyReportLoadGate({
    scopeWorkspaceId,
    reportQuery,
}: {
    scopeWorkspaceId: string | null | undefined;
    reportQuery: IntercompanyReportQueryGate | undefined;
}): { hasWorkspaceId: boolean; hasRequiredReportParams: boolean } {
    return {
        hasWorkspaceId: Boolean(scopeWorkspaceId),
        hasRequiredReportParams: Boolean(
            reportQuery?.workspaceId &&
            reportQuery.consolidationGroupId &&
            reportQuery.startDate &&
            reportQuery.endDate,
        ),
    };
}

export function getIntercompanyTransactionsPageLoadState({
    hasRequiredReportParams,
    hasWorkspaceId,
    isResolvingGroup,
    hasConsolidationGroup,
    detail,
    exceptions,
    hydration,
}: GetIntercompanyTransactionsPageLoadStateParams): IntercompanyTransactionsPageLoadState {
    if (!hasRequiredReportParams) {
        const shouldWaitForGroup =
            !hasWorkspaceId || isResolvingGroup || hasConsolidationGroup;
        return {
            shouldShowError: false,
            shouldShowLoader: shouldWaitForGroup,
            shouldShowReport: false,
            shouldShowStaleOverlay: false,
            shouldShowMissingGroup: !shouldWaitForGroup,
        };
    }
    if (detail.isError || exceptions.isError || hydration.isError) {
        return {
            shouldShowError: true,
            shouldShowLoader: false,
            shouldShowReport: false,
            shouldShowStaleOverlay: false,
            shouldShowMissingGroup: false,
        };
    }
    const isSuccess =
        detail.isSuccess && exceptions.isSuccess && hydration.isSuccess;
    const shouldShowStaleOverlay =
        isSuccess &&
        (Boolean(detail.isPlaceholderData) ||
            Boolean(exceptions.isPlaceholderData) ||
            Boolean(hydration.isPlaceholderData));
    return {
        shouldShowError: false,
        shouldShowLoader:
            detail.isLoading ||
            exceptions.isLoading ||
            hydration.isLoading ||
            !isSuccess,
        shouldShowReport: isSuccess,
        shouldShowStaleOverlay,
        shouldShowMissingGroup: false,
    };
}

export function toHydrationQueryLoadFlags({
    isHydrationRequired,
    isLoading,
    isError,
    isSuccess,
    isPlaceholderData,
}: {
    isHydrationRequired: boolean;
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    isPlaceholderData?: boolean;
}): IntercompanyQueryLoadFlags {
    if (!isHydrationRequired) {
        return {
            isLoading: false,
            isError: false,
            isSuccess: true,
            isPlaceholderData: false,
        };
    }
    return { isLoading, isError, isSuccess, isPlaceholderData };
}
